// src/contexts/CategoryContext.jsx
import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from 'react';
import api from '../api/axios';
import { useUser } from './UserContext';

const CategoryContext = createContext();

export const CategoryProvider = ({ children }) => {
    const { userInfo } = useUser(); // 로그인 여부

    /* ───────── 상태 ───────── */
    const [boardGroups, setBoardGroups] = useState([]);      // 트리 구조 (사이드바 전용)
    const [boardCategories, setBoardCats] = useState([]);    // 평탄 배열 (기존 컴포넌트 호환)
    const [chatCategories, setChatCats] = useState([]);

    /* ───────── 카테고리 로드 ───────── */
    const loadCategories = useCallback(() => {
        if (!userInfo) return; // 비로그인 시 패스

        /* 게시판 그룹 + 카테고리 */
        api.get('/api/boards/groups/')
            .then((res) => {
                // 백엔드는 관리자에게도 숨김 그룹을 내려줌.
                // ❗️사이드바에서는 "노출중" 인 그룹/게시판만 사용한다.
                const visibleGroups = (res.data.groups ?? [])
                    .map((g) => ({
                        ...g,
                        categories: (g.categories || []).filter((c) => c.is_active), // 게시판 노출 필터
                    }))
                    .filter((g) => g.is_active && g.categories.length) // 그룹 노출 & 빈 그룹 제외
                    .sort((a, b) => a.order - b.order);

                setBoardGroups(visibleGroups);

                // ── 평탄 배열 (기존 UI 호환) ────────────────────────
                const flat = visibleGroups
                    .flatMap((g) =>
                        g.categories.map((c) => ({
                            ...c, // id, name, order, is_active 등 유지
                            group: { id: g.id, name: g.name, emoji: g.emoji },
                        }))
                    )
                    .sort((a, b) =>
                        a.group.id === b.group.id ? a.order - b.order : 0,
                    );

                setBoardCats(flat);
            })
            .catch(console.error);

        /* 채팅 카테고리 */
        api.get('/api/chat/categories/')
            .then((res) => {
                const chats = (res.data.categories ?? [])
                    .filter((c) => c.is_active)
                    .sort((a, b) => a.order - b.order);
                setChatCats(chats);
            })
            .catch(console.error);
    }, [userInfo]);

    // 로그인 직후 1회 호출
    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    /* ───────── 컨텍스트 값 ───────── */
    return (
        <CategoryContext.Provider
            value={{
                boardGroups,      // 사이드바 트리 (노출중만)
                boardCategories,  // 평탄 배열 (노출중만)
                chatCategories,
                reloadCategories: loadCategories,
            }}
        >
            {children}
        </CategoryContext.Provider>
    );
};

export const useCategory = () => useContext(CategoryContext);
